import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  FormControlLabel,
  MenuItem,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AutorenewIcon, InsightsIcon, PendingActionsIcon, PlayCircleIcon, RefreshIcon, WarningIcon } from "../icons";
import { PageLayout } from "../common/layout";
import { useRootStore } from "../store/contexts/RootStoreContext";
import {
  AutomationStore,
  UMBRAL_ESTANCAMIENTO_DIAS,
  type EditionHealth,
} from "./automation/AutomationStore";
import { RetryWorkerEventModal } from "./automation/RetryWorkerEventModal";
import { InterventionCard } from "./automation/InterventionCard";
import { SignatureFailedCard } from "./automation/SignatureFailedCard";
import { AnalyticsTab } from "./automation/AnalyticsTab";
import { StatusChip } from "./common/StatusChip";
import { RetryIndicator } from "./common/RetryIndicator";

const fmtSince = (iso?: string | null): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "recién";
  if (mins < 60) return `hace ${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
};

const errText = (error: any): string =>
  error ? String(error?.message?.es ?? error?.message ?? error ?? "").slice(0, 160) : "";

/**
 * Mesa de operación del circuito automático (DNDA → SADAIC), organizada por
 * PRIORIDAD del operador: 1) qué requiere MI acción ahora (cards autocontenidas
 * para explorar/modificar/resolver in-situ), 2) qué se auto-recupera (vigilar),
 * 3) qué fluye normal, 4) analítica (ahora + histórico por períodos).
 */
export const AutomationPage = observer(() => {
  const rootStore = useRootStore();
  const store = useMemo(() => new AutomationStore(rootStore), [rootStore]);
  const isSysAdmin = !!(rootStore.authStore as any)?.hasRole?.("SysAdmin");

  useEffect(() => {
    if (!isSysAdmin) return;
    void store.fetchAll();
    // Sin auto-poll (rate limits): el admin refresca a mano.
  }, [store, isSysAdmin]);

  if (!isSysAdmin) {
    return (
      <PageLayout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h2">403</Typography>
          <Typography color="text.secondary">Esta herramienta es solo para SysAdmin.</Typography>
        </Box>
      </PageLayout>
    );
  }

  const intervention = store.interventionEditions;
  const signatureFailed = store.signatureFailedEditions;
  const recovering = store.recoveringEditions;
  const inProgress = store.inProgressEditions;
  // Ediciones pre-firma atascadas: en sentForSignature con TODAS las firmas completas
  // (el webhook de completitud no transicionó). allComplete las marca.
  const stuckPending = store.pendingEditions.filter((e: any) => e.allComplete);
  const stalled = store.stalledEditions;

  return (
    <PageLayout>
      <Stack spacing={2} sx={{ p: 3, width: "100%" }}>
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h2">Monitor de automación</Typography>
          <Tooltip title="Refrescar">
            <span>
              <IconButton onClick={() => void store.fetchAll()} disabled={store.loading}>
                {store.loading ? <CircularProgress size={22} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {store.error && (
          <Alert severity="error" onClose={() => (store.error = null)}>
            {store.error}
          </Alert>
        )}

        {/* Tarjetas resumen (clickeables → navegan a su pestaña) */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <SummaryCard label="Requieren intervención" value={store.interventionCount} kind="error" icon={<WarningIcon />} onClick={() => store.setTab(0)} selected={store.tab === 0} priority />
          <SummaryCard label="Auto-recuperando" value={recovering.length} kind="warning" icon={<AutorenewIcon />} onClick={() => store.setTab(1)} selected={store.tab === 1} />
          <SummaryCard label="En proceso" value={inProgress.length} kind="primary" icon={<PlayCircleIcon />} onClick={() => store.setTab(2)} selected={store.tab === 2} />
          <SummaryCard label="Firmas pendientes" value={stuckPending.length} kind="warning" icon={<PendingActionsIcon />} onClick={() => store.setTab(4)} selected={store.tab === 4} />
          <SummaryCard label="Analítica" value={null} kind="neutral" icon={<InsightsIcon />} onClick={() => store.setTab(3)} selected={store.tab === 3} />
        </Stack>

        {/* Pestañas */}
        <Tabs value={store.tab} onChange={(_, t) => store.setTab(t)} variant="scrollable">
          <Tab
            label={
              <Badge badgeContent={store.interventionCount} color="error" sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                Intervención humana
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={recovering.length} color="warning" sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                Auto-recuperando
              </Badge>
            }
          />
          <Tab label="En proceso" />
          <Tab label="Analítica" />
          <Tab
            label={
              <Badge badgeContent={stuckPending.length} color="warning" sx={{ "& .MuiBadge-badge": { right: -12 } }}>
                Firmas pendientes
              </Badge>
            }
          />
        </Tabs>

        {/* Tab 0 — Intervención humana (cards autocontenidas) */}
        {store.tab === 0 && (
          <Stack spacing={2}>
            <FiltrosIntervencion store={store} />

            {store.interventionCount === 0 && stalled.length === 0 ? (
              <Alert severity="success">Nada requiere intervención humana.</Alert>
            ) : (
              <>
                {signatureFailed.map((ed: any) => (
                  <SignatureFailedCard key={ed.id} store={store} edition={ed} />
                ))}
                {intervention.map((ed: any) => (
                  <InterventionCard key={ed.id} store={store} edition={ed} />
                ))}
                {store.botProblemsFiltrados
                  .filter((p) => !intervention.some((ed: any) => ed.id === p.run.relatedEntityId))
                  .map((p) => (
                    <BotProblemRow key={p.key} store={store} problema={p} />
                  ))}

                {store.soloVigentes && store.botProblemsHistoricos > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ alignSelf: "flex-start" }}
                    onClick={() => store.setSoloVigentes(false)}
                  >
                    Ver {store.botProblemsHistoricos} intento
                    {store.botProblemsHistoricos === 1 ? "" : "s"} de problemas ya resueltos
                  </Button>
                )}

                <StalledSection store={store} />
              </>
            )}
          </Stack>
        )}

        {/* Tab 1 — Auto-recuperando (vigilancia) */}
        {store.tab === 1 && (
          <EditionsTable store={store} editions={recovering} empty="— nada auto-recuperando —" showRetry />
        )}

        {/* Tab 2 — En proceso (+ panorama completo) */}
        {store.tab === 2 && (
          <Stack spacing={2}>
            <EditionsTable store={store} editions={inProgress} empty="— nada en proceso —" />
            <Typography variant="h4">Todas las ediciones</Typography>
            <EditionsTable store={store} editions={store.allEditions} empty="— sin ediciones —" showHealth />
          </Stack>
        )}

        {/* Tab 3 — Analítica */}
        {store.tab === 3 && <AnalyticsTab store={store} />}

        {/* Tab 4 — Firmas pendientes (ediciones pre-firma en sentForSignature) */}
        {store.tab === 4 && (
          <Stack spacing={2}>
            <Typography variant="h4">Ediciones esperando firma — completitud</Typography>
            {store.pendingEditions.length === 0 ? (
              <Alert severity="success">No hay ediciones esperando firmas.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Edición</TableCell>
                      <TableCell>Versión</TableCell>
                      <TableCell>Firmados</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {store.pendingEditions.map((ed: any) => (
                      <TableRow key={ed.editionId}>
                        <TableCell>{ed.name ?? ed.editionId}</TableCell>
                        <TableCell>{ed.version}</TableCell>
                        <TableCell>{ed.completedDocs}/{ed.totalDocs}</TableCell>
                        <TableCell>
                          {ed.allComplete && <Chip size="small" color="error" label="Atascada" sx={{ mr: 0.5 }} />}
                          {ed.unlinkedSignatures > 0 && <Chip size="small" color="warning" label={`${ed.unlinkedSignatures} sin vincular`} />}
                          {!ed.allComplete && ed.unlinkedSignatures === 0 && <Chip size="small" variant="outlined" label="En firma" />}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={store.recheckingEdition === ed.editionId}
                            onClick={() => void store.recheckEdition(ed.editionId)}
                            startIcon={store.recheckingEdition === ed.editionId ? <CircularProgress size={14} /> : undefined}
                          >
                            Re-verificar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        )}
      </Stack>

      <RetryWorkerEventModal store={store} />
    </PageLayout>
  );
});

const SummaryCard = ({
  label,
  value,
  kind,
  icon,
  onClick,
  selected,
  priority,
}: {
  label: string;
  value: number | null;
  kind: "error" | "warning" | "primary" | "neutral";
  icon: React.ReactElement;
  onClick: () => void;
  selected: boolean;
  priority?: boolean;
}) => {
  const theme = useTheme();
  const color = kind === "neutral" ? theme.palette.text.secondary : theme.palette[kind].main;
  const alert = priority && (value ?? 0) > 0;
  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: 170,
        flex: 1,
        borderColor: selected || alert ? color : undefined,
        borderWidth: selected || alert ? 2 : 1,
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {React.cloneElement(icon, { sx: { color } } as any)}
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Stack>
          <Typography variant="h1" sx={{ color, lineHeight: 1.3 }}>
            {value ?? "→"}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/** Tabla liviana de ediciones (recuperando / en proceso / panorama). */
const EditionsTable = observer(
  ({
    store,
    editions,
    empty,
    showRetry,
    showHealth,
  }: {
    store: AutomationStore;
    editions: any[];
    empty: string;
    showRetry?: boolean;
    showHealth?: boolean;
  }) => {
    if (editions.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      );
    }
    return (
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Edición</TableCell>
              <TableCell>Workflow DNDA</TableCell>
              <TableCell>Workflow SADAIC</TableCell>
              {showRetry && <TableCell>Reintento</TableCell>}
              {showRetry && <TableCell>Último error</TableCell>}
              {showHealth && <TableCell>Salud</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {editions.map((ed: any) => {
              const ri = showRetry ? store.retryInfoFor(ed) : null;
              const dl = showRetry ? store.deadLetterForEdition(ed.id) : null;
              return (
                <TableRow key={ed.id} hover>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {ed?.data_?.name ?? ed.displayId ?? ed.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: store.isFailureState(ed?.dndaState?.id) ? "error.main" : "text.primary" }}>
                      {ed?.dndaState?.id ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: store.isFailureState(ed?.sadaicState?.id) ? "error.main" : "text.primary" }}>
                      {ed?.sadaicState?.id ?? "—"}
                    </Typography>
                  </TableCell>
                  {showRetry && (
                    <TableCell>
                      {ri ? <RetryIndicator retryCount={ri.attempt} nextRetryAt={ri.nextRetryAt} /> : "—"}
                    </TableCell>
                  )}
                  {showRetry && (
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={errText(dl?.error)}>
                        <Typography variant="body2" color="error" noWrap>
                          {errText(dl?.error) || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  {showHealth && (
                    <TableCell>
                      <StatusChip status={store.healthFor(ed) as EditionHealth} />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
);

/** Barra de filtros de la pestaña de intervención. */
const FiltrosIntervencion = observer(({ store }: { store: AutomationStore }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
    <TextField
      size="small"
      label="Buscar edición"
      value={store.filtroTexto}
      onChange={(e) => store.setFiltroTexto(e.target.value)}
      sx={{ minWidth: 220 }}
    />
    <TextField
      size="small"
      select
      label="Bot"
      value={store.filtroBot}
      onChange={(e) => store.setFiltroBot(e.target.value)}
      sx={{ minWidth: 180 }}
    >
      <MenuItem value="">Todos</MenuItem>
      {store.botTypesEnProblemas.map((b) => (
        <MenuItem key={b} value={b}>
          {b}
        </MenuItem>
      ))}
    </TextField>
    <FormControlLabel
      control={
        <Switch
          checked={store.soloVigentes}
          onChange={(e) => store.setSoloVigentes(e.target.checked)}
        />
      }
      label="Solo lo que sigue trabado"
    />
  </Stack>
));

/**
 * Un problema de bot: una entidad, un bot, el último intento y cuántos hubo.
 *
 * Antes era una fila por BotRun. Como el estado `requires_intervention` no se
 * limpia nunca —es forense, no un estado de problema— la misma edición
 * aparecía cuatro veces por el mismo error, junto a corridas de hace dos
 * semanas sobre ediciones que ya habían seguido. Eran 39 renglones y casi
 * ninguno pedía una decisión.
 */
const BotProblemRow = observer(
  ({
    store,
    problema,
  }: {
    store: AutomationStore;
    problema: { key: string; run: any; intentos: number; primero: string | null; vigente: boolean };
  }) => {
    const { router } = useRootStore();
    const { run, intentos, vigente } = problema;
    return (
      <Card
        variant="outlined"
        sx={{ borderColor: vigente ? "warning.main" : "divider", opacity: vigente ? 1 : 0.65 }}
      >
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h4">
              {store.nameForBotRun(run) ?? run.relatedEntityId?.slice(0, 8) ?? "—"}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }} color="text.secondary">
              {run.botType}
            </Typography>
            {/* El estado del BOT, no el del WorkerEvent: el job puede figurar
                `completed` sobre un bot que quedó pidiendo intervención. */}
            <StatusChip status={run.status} />
            {intentos > 1 && (
              <Tooltip title={`El primero, ${fmtSince(problema.primero)}`}>
                <Chip size="small" label={`${intentos} intentos`} />
              </Tooltip>
            )}
            {!vigente && (
              <Tooltip title="La edición ya no está en un estado de falla: quedó como registro del intento.">
                <Chip size="small" variant="outlined" label="ya resuelto" />
              </Tooltip>
            )}
            <Typography variant="body2" color="text.secondary">
              {run.currentPhase ?? ""}
            </Typography>
            {run.interventionReason && (
              <Typography variant="body2" color={vigente ? "error" : "text.secondary"}>
                {run.interventionReason}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {fmtSince(run.startedAt)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", color: "primary.main" }}
              onClick={() => router.goTo(`/simulation?editionId=${run.relatedEntityId}`)}
            >
              ir a /simulation →
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }
);

/**
 * Ediciones quietas en un estado estable.
 *
 * No es un estado de falla, así que no aparecía en ninguna parte del panel — y
 * es exactamente así como se pierde una edición: seis días sin error, sin
 * worker y sin figurar. Las que declararon `.awaits` en el DSL no entran acá:
 * esperar el comprobante de la DNDA por meses es lo esperado, no un problema.
 */
const StalledSection = observer(({ store }: { store: AutomationStore }) => {
  const { router } = useRootStore();
  const stalled = store.stalledEditions;
  if (stalled.length === 0) return null;
  return (
    <Stack spacing={1}>
      <Typography variant="h4">Quietas sin explicación</Typography>
      <Alert severity="warning" sx={{ py: 0.5 }}>
        Sin falla, sin worker y sin moverse hace más de {UMBRAL_ESTANCAMIENTO_DIAS} días. Nadie las
        está esperando.
      </Alert>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Edición</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Quieta hace</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {stalled.map(({ ed, dias, estado }) => (
              <TableRow key={ed.id}>
                <TableCell>{ed?.data_?.name ?? ed.displayId ?? ed.id.slice(0, 8)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{estado}</TableCell>
                <TableCell align="right">{dias} d</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{ cursor: "pointer", color: "primary.main" }}
                    onClick={() => router.goTo(`/simulation?editionId=${ed.id}`)}
                  >
                    ir a /simulation →
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
});
